import { EVENTS } from '../../constants';
import type { User } from '../../types/user';

export type AvatarPickSource = 'library' | 'camera' | 'file';

const PICK_METHOD: Record<AvatarPickSource, string> = {
  library: 'library',
  camera: 'take photo',
  file: 'choose file',
};

type TrackFn = (eventName: string, properties?: Record<string, unknown>) => void;

interface UploadPhotoClickParams {
  source: AvatarPickSource;
  targetUserId?: string;
}

interface UploadPhotoParams {
  source: AvatarPickSource;
  newPhotoId?: string;
  previousPhotoId?: string | null;
  targetUserId?: string;
}

interface RemovePhotoParams {
  photoId?: string;
  targetUserId?: string;
}

export const createProfilePhotoReporter = (track: TrackFn) => ({
  editPhotoClick: () => track(EVENTS.USER_PROFILE.EDIT_PHOTO_CLICK),

  uploadPhotoClick: ({ source, targetUserId }: UploadPhotoClickParams) =>
    track(EVENTS.USER_PROFILE.UPLOAD_PHOTO_CLICK, {
      method: PICK_METHOD[source],
      targetUserId,
    }),

  uploadPhoto: ({ source, newPhotoId, previousPhotoId, targetUserId }: UploadPhotoParams) =>
    track(EVENTS.USER_PROFILE.UPLOAD_PHOTO, {
      method: PICK_METHOD[source],
      photoId: newPhotoId,
      ...(previousPhotoId && { replaced: true, replacedId: previousPhotoId }),
      targetUserId,
    }),

  removePhotoClick: ({ photoId, targetUserId }: RemovePhotoParams) =>
    track(EVENTS.USER_PROFILE.REMOVE_PHOTO_CLICK, {
      photoId,
      targetUserId,
    }),

  removePhotoSubmit: ({ photoId, targetUserId }: RemovePhotoParams) =>
    track(EVENTS.USER_PROFILE.REMOVE_PHOTO_SUBMIT, {
      photoId,
      targetUserId,
    }),

  contactsProfilePhotoOpen: (user: Pick<User, 'id' | 'avatarPublicId'>) =>
    track(EVENTS.CONTACT.PROFILE_PHOTO_OPEN, {
      contactId: user.id,
      photoId: user.avatarPublicId,
    }),
});

export type ProfilePhotoReporter = ReturnType<typeof createProfilePhotoReporter>;
